<script setup>
import {computed, onMounted, ref} from "vue";
import {route} from "ziggy-js";
import {router} from "@inertiajs/vue3";
import {useI18n} from 'vue-i18n';
import {usePage} from "@inertiajs/vue3";

const {t} = useI18n();
const page = usePage();
const currentTeam = page.props.team;

const productsExpanded = ref(false);
const activePath = ref('');

const removeLastPartUrl = (url) => {
    const urlParts = url.split('/');
    if (urlParts.length > 1) {
        urlParts.pop();
        return urlParts.join('/');
    }
    return url;
};

const cleanUrl = (url) => {
    return url && url.length > 10 ? url.replace(/^(https?:\/\/)?(www\.)?[^/]+/, '') : url;
};

const isActive = (url, alternativeUrls = []) => {
    if (!url) {
        return false;
    }

    const cleanedUrl = cleanUrl(url);
    if (activePath.value === cleanedUrl) {
        return true;
    }

    return alternativeUrls.some((altUrl) => activePath.value.startsWith(cleanUrl(altUrl)));
};

const mainItems = computed(() => {
    if (!currentTeam) {
        return [];
    }

    const items = [
        {
            key: 'dashboard',
            label: t('admin.menu.dashboard'),
            icon: 'pi pi-home',
            url: route('admin.dashboard'),
        },
        {
            key: 'sessions',
            label: t('admin.menu.sessions'),
            icon: 'pi pi-list',
            url: route('admin.team.sessions.index', {team: currentTeam.id}),
            alternativeUrls: [
                removeLastPartUrl(route('admin.team.session.edit', {team: currentTeam.id, teamSession: 0})),
            ],
        },
        {
            key: 'products',
            label: t('admin.menu.products'),
            icon: 'pi pi-box',
            url: route('admin.products.index'),
            alternativeUrls: [
                removeLastPartUrl(route('admin.products.show', {team: currentTeam.id, product: 0})),
            ],
            expandable: page.props.isSuperAdmin,
            children: page.props.isSuperAdmin ? [
                {
                    key: 'products-list',
                    label: t('admin.menu.products'),
                    url: route('admin.products.index'),
                    alternativeUrls: [
                        removeLastPartUrl(route('admin.products.show', {team: currentTeam.id, product: 0})),
                    ],
                },
                {
                    key: 'library',
                    label: t('admin.menu.library'),
                    url: route('admin.library.index'),
                },
            ] : [],
        },
        {
            key: 'icons',
            label: t('admin.menu.icons'),
            icon: 'pi pi-image',
            url: route('admin.vector-images.index'),
        },
    ];

    if (page.props.permissions?.canManageTeams) {
        items.push({
            key: 'teams',
            label: t('admin.menu.teams'),
            icon: 'pi pi-users',
            url: route('admin.teams.index'),
        });
    } else {
        items.push({
            key: 'team',
            label: t('admin.menu.team'),
            icon: 'pi pi-users',
            url: route('admin.teams.show', {teamId: currentTeam.id}),
        });
    }

    return items;
});

const settingsUrl = computed(() => route('admin.user.show'));
const documentationUrl = computed(() => route('public.models'));

const isSettingsActive = computed(() => isActive(settingsUrl.value));

const toggleProducts = () => {
    productsExpanded.value = !productsExpanded.value;
};

const logout = () => {
    router.post(route('admin.perform-logout'));
};

onMounted(() => {
    activePath.value = cleanUrl(window.location.pathname);

    const productsItem = mainItems.value.find((item) => item.key === 'products');
    if (productsItem?.children?.length) {
        const productsActive = isActive(productsItem.url, productsItem.alternativeUrls);
        const libraryActive = productsItem.children.some((child) => isActive(child.url, child.alternativeUrls));

        if (productsActive || libraryActive) {
            productsExpanded.value = true;
        }
    }
});
</script>

<template>
    <aside class="admin-menu">
        <nav class="admin-menu__nav" aria-label="Admin navigation">
            <ul class="admin-menu__list">
                <li
                    v-for="item in mainItems"
                    :key="item.key"
                    class="admin-menu__item"
                >
                    <a
                        v-if="!item.expandable"
                        :href="item.url"
                        class="admin-menu__link"
                        :class="{ active: isActive(item.url, item.alternativeUrls) }"
                    >
                        <i :class="['admin-menu__icon', item.icon]" aria-hidden="true"/>
                        <span>{{ item.label }}</span>
                    </a>

                    <template v-else>
                        <button
                            type="button"
                            class="admin-menu__toggle"
                            :class="{ active: isActive(item.url, item.alternativeUrls) || item.children.some(child => isActive(child.url, child.alternativeUrls)) }"
                            :aria-expanded="productsExpanded"
                            @click="toggleProducts"
                        >
                            <i :class="['admin-menu__icon', item.icon]" aria-hidden="true"/>
                            <span>{{ item.label }}</span>
                            <i
                                :class="['admin-menu__chevron pi', productsExpanded ? 'pi-angle-down' : 'pi-angle-right']"
                                aria-hidden="true"
                            />
                        </button>

                        <ul v-if="productsExpanded" class="admin-menu__sublist">
                            <li
                                v-for="child in item.children"
                                :key="child.key"
                                class="admin-menu__item"
                            >
                                <a
                                    :href="child.url"
                                    class="admin-menu__link"
                                    :class="{ active: isActive(child.url, child.alternativeUrls) }"
                                >
                                    <span>{{ child.label }}</span>
                                </a>
                            </li>
                        </ul>
                    </template>
                </li>
            </ul>
        </nav>

        <div class="admin-menu__bottom">
            <section class="admin-menu__section" aria-label="Account">
                <div class="admin-menu__section-label admin-eyebrow">
                    {{ t('admin.menu.account') }}
                </div>
                <ul class="admin-menu__sublist">
                    <li class="admin-menu__item">
                        <a
                            :href="settingsUrl"
                            class="admin-menu__link"
                            :class="{ active: isSettingsActive }"
                        >
                            <i class="admin-menu__icon pi pi-cog" aria-hidden="true"/>
                            <span>{{ t('admin.menu.settings') }}</span>
                        </a>
                    </li>
                    <li class="admin-menu__item">
                        <button
                            type="button"
                            class="admin-menu__link"
                            @click="logout"
                        >
                            <i class="admin-menu__icon pi pi-sign-out" aria-hidden="true"/>
                            <span>{{ t('admin.menu.logout') }}</span>
                        </button>
                    </li>
                </ul>
            </section>

            <a
                :href="documentationUrl"
                class="admin-menu__link"
                target="_blank"
                rel="noopener noreferrer"
            >
                <i class="admin-menu__icon pi pi-external-link" aria-hidden="true"/>
                <span>{{ t('admin.menu.documentation') }}</span>
            </a>
        </div>
    </aside>
</template>

<style scoped lang="scss">
@use '@/Pages/Admin/Common/AdminMenu.scss';
</style>